import './lib/interfaces.js';
/**
 * AnalyticsService class provides methods to initialize, track, and manage website analytics.
 */
export class AnalyticsService {
    /**
    * Initializes the AnalyticsService with provided configuration.
    * @param config - Configuration object specifying debug mode, environment, and base URL.
    */
    constructor(config) {
        /**
         * Indicates whether analytics is enabled.
         * @default true
         */
        this.enabled = true;
        /**
         * Indicates whether detailed logging is enabled. Useful for debugging.
         * @default false
         */
        this.loggingEnabled = false;
        this._shadowDomClickElements = [];
        this.loggingEnabled = config.debugMode;
        this.env = config.environment;
        this.basePath = config.baseUrl;
    }
    /**
    * Initializes analytics tracking if enabled.
    * This method loads a consent script (if necessary) and the main tracking script.
    */
    init() {
        if (this.enabled) {
            this._setupConsentCallback();
        }
        else {
            this.log('Analytics Disabled');
        }
    }
    /**
     * Shuts down analytics tracking by removing click handlers.
     * This method is useful for scenarios where tracking needs to be stopped dynamically.
     */
    shutdown() {
        if (this.enabled) {
            this._removeClickHandlers();
        }
    }
    get _customElements() {
        return document.querySelectorAll('[data-analytics="true"]');
    }
    // Helper function to get all the elements in the light and shadow DOM with the matching data attribute
    // and return them as an Array of HTMLELements for further processing.
    get _clickElements() {
        const lightDomElements = document.querySelectorAll('[data-track="click"]');
        const shadowDomElements = [];
        this._customElements.forEach((customElement) => {
            const internalElements = customElement.shadowRoot?.querySelectorAll('[data-track="click"]');
            shadowDomElements.push(...Array.from(internalElements));
        });
        return [...Array.from(lightDomElements), ...shadowDomElements];
    }
    // If the links are to other Xing domains or the same domain we should decorate the link
    // with the appropriate URL parameters instead. This function handles that process.
    _decorateXingLinks() {
        this._clickElements.forEach((link) => {
            if (link.hasAttribute('href')) {
                // Some links might have a special [data-internal="false"] attribute on them to indicate 
                // that they should NOT use the URL decoration approach but instead be treated as though 
                // they were links to external pages we don't control for whatever reason. 
                // This is where we check for that and avoid decorating them.
                if (link.dataset.internal === 'false') {
                    return;
                }
                // We know its a link
                //@ts-ignore
                const parsedLink = new URL(link.href);
                if (parsedLink.hostname.includes('xing.com') || parsedLink.origin === window.location.origin) {
                    this.log(`Internal link detected: ${parsedLink.href}`);
                    if (link.dataset.analyticsOrigin !== undefined) {
                        parsedLink.searchParams.set('sc_o_PropActionOrigin', link.dataset.analyticsOrigin);
                    }
                    if (link.dataset.analyticsAction !== undefined) {
                        parsedLink.searchParams.set('sc_o', link.dataset.analyticsAction);
                    }
                    // Update the link with the correct URL parameters
                    link.setAttribute('href', parsedLink.href);
                    this.log(`Link decorated: ${parsedLink.href}`);
                    // Ensure we won't pick it up on future queries for analytics links
                    link.dataset.track = 'internal-click';
                }
            }
        });
    }
    _loadTracking() {
        this.log('Loading tracking');
        const scriptTag = document.createElement('script');
        scriptTag.src = `${this.basePath}/includes/analytics/inline-tracking.js`;
        scriptTag.type = 'text/javascript';
        scriptTag.dataset.usercentrics = 'Adobe Analytics';
        scriptTag.onload = () => this._initializeTracking();
        document.body.insertAdjacentElement('beforeend', scriptTag);
    }
    // Set up all the relevant click handlers
    _installClickHandlers() {
        this.log('Installing handlers');
        this.log(document.readyState);
        this._lightDomClickElements = document.querySelectorAll('[data-track="click"]');
        this._lightDomClickElements.forEach(element => {
            element.addEventListener('click', (e) => this._onTrackedClick(e));
        });
        this._customElements?.forEach(element => {
            if (element.shadowRoot === null) {
                this.log(`The following element is missing a shadowRoot:\n ${element.tagName}`);
                return;
            }
            const elementsArray = element.shadowRoot.querySelectorAll('[data-track="click"]');
            elementsArray.forEach(clickElement => {
                clickElement.addEventListener('click', (e) => this._onTrackedClick(e));
            });
            // Update the global list so we can remove the click handlers later
            this._shadowDomClickElements.push(...Array.from(elementsArray));
        });
    }
    _removeClickHandlers() {
        this._lightDomClickElements?.forEach(element => {
            element.removeEventListener('click', (e) => this._onTrackedClick(e));
        });
        if (this._shadowDomClickElements.length > 0) {
            this._shadowDomClickElements.forEach((clickElement) => {
                clickElement.removeEventListener('click', (e) => this._onTrackedClick(e));
            });
        }
    }
    _onTrackedClick(e) {
        this.log('Processing click event:\n', e);
        const clickedElement = e.target;
        const analyticsElement = clickedElement.closest('[data-track="click"]');
        if (analyticsElement === null) {
            this.log('Unable to find relevant analytics tag');
            return;
        }
        const analyticsAction = analyticsElement.dataset.analyticsAction;
        const analyticsOrigin = analyticsElement.dataset.analyticsOrigin;
        const analyticsItemId = analyticsElement.dataset.analyticsItemId;
        this.log(`Analytics Action: ${analyticsAction} \nAnalytics Origin: ${analyticsOrigin} \nAnalytics Item: ${analyticsItemId}`);
        if (analyticsAction && analyticsOrigin && analyticsItemId) {
            window.ttt?.event('PropTrackAction', {
                PropTrackAction: analyticsAction,
                PropActionOrigin: analyticsOrigin,
                PropItemId: analyticsItemId,
            });
            return;
        }
        else if (analyticsAction && analyticsOrigin) {
            window.ttt?.event('PropTrackAction', {
                PropTrackAction: analyticsAction,
                PropActionOrigin: analyticsOrigin,
            });
        }
    }
    // Set up all the relevant intersection observer handlers
    _installObservationHandlers() {
        let observedElements = Array.from(document.querySelectorAll('[data-track="visible"]'));
        this.log(`Observed elements \n ${observedElements}`);
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    const element = entry.target;
                    const analyticsScrolling = element.dataset.analyticsScrolling;
                    const analyticsModule = element.dataset.analyticsModule;
                    if (analyticsModule && analyticsScrolling) {
                        window.ttt?.event('PropScrolling', {
                            PropModules: analyticsModule,
                            PropScrolling: analyticsScrolling
                        });
                    }
                    // Remove the 'data-track' attribute to log only once per element
                    element.removeAttribute('data-track');
                    // Disconnect the observer for this element
                    observer.unobserve(element);
                }
            });
        });
        observedElements.forEach(element => {
            observer.observe(element);
        });
    }
    _initializeTracking() {
        this.log('Initializing tracking');
        if ('ttt' in window) {
            window.ttt.init(this.env, { clearParams: true });
            this.log('tracking initialized');
            const propChannel = document.querySelector('meta[name="tracking-channel"]');
            const pageName = document.querySelector('meta[name="tracking-page"]');
            if (propChannel && pageName) {
                window.ttt.pageview(propChannel.content, pageName.content, this._generateContextParams());
                this.log(this);
                this._decorateXingLinks();
                this._installClickHandlers();
                this._installObservationHandlers();
            }
            else {
                this.log('Unable to initialize tracking. Meta tags not found.');
            }
        }
    }
    // Some pages require an extra context property. We leverage Wordpress's 
    // class names as a way of determining what values under
    // what circumstances make sense otherwise we just return a blank object.
    _generateContextParams() {
        const postAnalyticsCategory = this._getPostAnalyticsCategory();
        if (postAnalyticsCategory === null) {
            return {};
        }
        else {
            return { PropContextDimension1: postAnalyticsCategory };
        }
    }
    // Checks the page for any category links and uses that to return the appropriate analytics context prop
    _getPostAnalyticsCategory() {
        try {
            const categoryLink = document.querySelector('a[rel="category tag"]');
            if (categoryLink === null) {
                return null;
            }
            else {
                const translatedCategories = new Map([
                    ["Hintergründe", "xing_lp_baller_background"],
                    ["Analysen", "xing_lp_baller_analysis"],
                    ["Interviews", "xing_lp_baller_interview"]
                ]);
                if (translatedCategories.has(categoryLink.innerText)) {
                    return translatedCategories.get(categoryLink.innerText);
                }
                else {
                    return null;
                }
            }
        }
        catch (error) {
            return null;
        }
    }
    /**
     * Installs analytics tracking based on user consent.
     *
     * This function listens for the `ucConsentEvent` dispatched by Usercentrics
     * when the user interacts with the consent prompt. It then retrieves the
     * current consent details and logs them to the console.
     *
     */
    _setupConsentCallback() {
        /**
         * Event listener for the `ucConsentEvent`.
         *
         * This listener is triggered when the user interacts with the Usercentrics
         * consent prompt. It retrieves the current consent details from the event
         * and logs them to the console.
         *
         * @param {Event} event - The `ucConsentEvent` object containing consent details.
         */
        window.addEventListener("ucConsentEvent", (event) => {
            this.log('Processing consent event');
            /**
             * The current consent details.
             *
             * This object contains information about the user's consent choices,
             * as provided by Usercentrics.
             *
             * @type {Object}
             */
            let currentConsent = event.detail;
            if (this._haveValidConsent(currentConsent)) {
                this._loadTracking();
            }
            else {
                console.info("User has not given valid consent. Analytics disabled.");
            }
        });
    }
    /**
     * Checks if the user has given valid consent needed for analytics.
     *
     * @param {object} ucConsentStatus object
     * @returns {boolean} if the consent is valid
     */
    _haveValidConsent(ucConsentStatus) {
        const explicitConsent = ucConsentStatus['type'] === 'explicit';
        const adobeEnabled = ucConsentStatus['Adobe Analytics'] === true;
        this.log(`Explicit Consent: ${explicitConsent} \nAdobe Enabled: ${adobeEnabled}`);
        return explicitConsent && adobeEnabled;
    }
    log(...data) {
        if (this.loggingEnabled === true) {
            console.log(...data);
        }
    }
}
//# sourceMappingURL=service.js.map